import { useQueryClient, type UseQueryOptions } from 'react-query'

import {
  apiPaths,
  queryKeys,
  validateStatus,
  handleError,
} from 'api/kinerja/penilik-pegawai/config'
import { useMutationApi, useQueryApi } from 'utils/api'

import type {
  PelaksanaanPerilakuKerja,
  PelaksanaanPengembanganKompetensi,
  PenilaianPengembanganKompetensiSubmissionBody,
  PenilaianPengembanganKompetensiSubmissionResponse,
  PelaksanaanPraktikKinerjaSummary,
  PelaksanaanTugasResponse,
  SubmissionResponse,
} from 'types/kinerja-penilik-pegawai'
import type { SubmitRatingPelaksanaanPraktikKinerjaBody } from 'types/kinerja-tim-penilai-penilik'
import type { PraktikKinerjaSurveyPendampingan } from 'types/kinerja-penilik-pegawai'
import type { AxiosError } from 'axios'

export function useAPIGetPelaksanaanPerilakuKerja({
  skpId,
  ...options
}: UseQueryOptions<PelaksanaanPerilakuKerja> & { skpId: string }) {
  return useQueryApi<PelaksanaanPerilakuKerja>(
    `${apiPaths.pelaksanaan}/perilaku-kerja/${skpId}`,
    {
      queryKey: [queryKeys.API_GET_PELAKSANAAN_PERILAKU_KERJA, skpId],
      axiosConfig: {
        validateStatus,
      },
      queryConfig: {
        useErrorBoundary: true,
        staleTime: 60 * 60 * 1000, // 1 hour
        cacheTime: 60 * 60 * 1000, // 1 hour
        ...options,
      },
      errorHandlerFn: handleError,
    }
  )
}

export function useAPIGetPelaksanaanPengembanganKompetensi({
  skpId,
  ...options
}: UseQueryOptions<PelaksanaanPengembanganKompetensi> & { skpId: string }) {
  return useQueryApi<PelaksanaanPengembanganKompetensi>(
    `${apiPaths.pelaksanaan}/pengembangan-kompetensi/${skpId}`,
    {
      queryKey: [queryKeys.API_GET_PELAKSANAAN_PENGEMBANGAN_KOMPETENSI, skpId],
      axiosConfig: {
        validateStatus,
      },
      queryConfig: {
        useErrorBoundary: true,
        staleTime: 60 * 60 * 1000, // 1 hour
        cacheTime: 60 * 60 * 1000, // 1 hour
        ...options,
      },
      errorHandlerFn: handleError,
    }
  )
}

export function useAPISubmitPelaksanaanPengembanganKompetensi({ skpId }) {
  const queryClient = useQueryClient()

  return useMutationApi<
    PenilaianPengembanganKompetensiSubmissionResponse,
    AxiosError,
    PenilaianPengembanganKompetensiSubmissionBody
  >(`${apiPaths.pelaksanaan}/pengembangan-kompetensi/${skpId}`, {
    axiosConfig: { method: 'POST' },
    mutationConfig: {
      onSettled() {
        queryClient.invalidateQueries([
          queryKeys.API_GET_PELAKSANAAN_PENGEMBANGAN_KOMPETENSI,
          skpId,
        ])
      },
    },
  })
}

// Get Summary of Praktik Kinerja
export const useAPIGetPraktikKinerjaPelaksanaanSummary = ({
  skpId,
  opts,
}: {
  skpId: string
  opts?: UseQueryOptions<PelaksanaanPraktikKinerjaSummary>
}) => {
  return useQueryApi(
    `${apiPaths.pelaksanaan}/praktik-kinerja/${skpId}/summary`,
    {
      axiosConfig: { validateStatus },
      queryConfig: { useErrorBoundary: false, ...opts },
      queryKey: [queryKeys.API_GET_PRAKTIK_KINERJA_PELAKSANAAN_SUMMARY, skpId],
    }
  )
}

export const useAPIGetPelaksanaanTugas = ({
  skpId,
  opts,
}: {
  skpId: string
  opts?: UseQueryOptions<PelaksanaanTugasResponse>
}) => {
  return useQueryApi(apiPaths.pelaksanaanTugas(skpId), {
    axiosConfig: { validateStatus },
    queryConfig: {
      useErrorBoundary: false,
      staleTime: 60 * 60 * 1000, // 1 hour
      cacheTime: 60 * 60 * 1000, // 1 hour
      ...opts,
    },
    queryKey: [queryKeys.API_GET_PELAKSANAAN_TUGAS, skpId],
  })
}

export const useAPIPostPelaksanaanTugas = ({ skpId }: { skpId: string }) => {
  const queryClient = useQueryClient()

  return useMutationApi<SubmissionResponse, AxiosError, {}>(
    `${apiPaths.pelaksanaanTugas(skpId)}`,
    {
      axiosConfig: { method: 'POST' },
      mutationConfig: {
        onSettled() {
          queryClient.invalidateQueries([
            queryKeys.API_GET_PELAKSANAAN_TUGAS,
            skpId,
          ])
        },
      },
    }
  )
}

export const useAPIGetSurveyPendampingan = ({ skpId }: { skpId: string }) => {
  return useQueryApi<PraktikKinerjaSurveyPendampingan>(
    `${apiPaths.pelaksanaan}/praktik-kinerja/${skpId}/survey`,
    {
      queryKey: [
        queryKeys.API_GET_PRAKTIK_KINERJA_PELAKSANAAN_SURVEY_PENDAMPINGAN,
        skpId,
      ],
    }
  )
}

export const useAPIPostSubmitSurveyPendampingan = ({
  skpId,
}: {
  skpId: string
}) => {
  const queryClient = useQueryClient()

  return useMutationApi<
    SubmissionResponse,
    AxiosError,
    SubmitRatingPelaksanaanPraktikKinerjaBody
  >(`${apiPaths.pelaksanaan}/praktik-kinerja/${skpId}/survey`, {
    axiosConfig: { method: 'POST' },
    mutationConfig: {
      onSettled() {
        queryClient.invalidateQueries([
          queryKeys.API_GET_PRAKTIK_KINERJA_PELAKSANAAN_SURVEY_PENDAMPINGAN,
          skpId,
        ])
      },
    },
  })
}
